import { useMemo } from 'react'
import { Box, Tooltip, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import type { HeatmapPoint } from '@/types/dashboard'

interface HeatmapGridProps {
  from: string
  to: string
  days: HeatmapPoint[]
  /** Компактный режим (виджет streak): меньше клетки */
  compact?: boolean
}

/** Прозрачность заливки по уровню 0–4 (0 — пустая клетка) */
const LEVEL_ALPHA = [0, 0.18, 0.4, 0.65, 1]

/**
 * GitHub-style тепловая карта активности: колонки — недели, строки — дни недели.
 * Рендерится на CSS-grid без сторонних chart-библиотек.
 */
export function HeatmapGrid({ from, to, days, compact = false }: HeatmapGridProps) {
  const theme = useTheme()
  const { t } = useTranslation()

  const { weeks, pointsByDate } = useMemo(() => {
    const pointsMap = new Map(days.map((point) => [point.date, point]))
    // Начало сетки — понедельник недели, в которую попадает from
    const start = dayjs(from).startOf('week')
    const end = dayjs(to)

    const allWeeks: string[][] = []
    let cursor = start
    while (cursor.isBefore(end) || cursor.isSame(end, 'day')) {
      const week: string[] = []
      for (let day = 0; day < 7; day++) {
        week.push(cursor.format('YYYY-MM-DD'))
        cursor = cursor.add(1, 'day')
      }
      allWeeks.push(week)
    }
    return { weeks: allWeeks, pointsByDate: pointsMap }
  }, [days, from, to])

  const cellSize = compact ? 8 : 11
  const gap = 2

  return (
    <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
      <Box sx={{ display: 'flex', gap: `${gap}px`, width: 'max-content' }}>
        {weeks.map((week, weekIndex) => (
          <Box key={weekIndex} sx={{ display: 'flex', flexDirection: 'column', gap: `${gap}px` }}>
            {week.map((date) => {
              const point = pointsByDate.get(date)
              const level = point?.level ?? 0
              const inRange = date >= from && date <= to
              const backgroundColor = !inRange
                ? 'transparent'
                : level === 0
                  ? alpha(theme.palette.text.primary, 0.06)
                  : alpha(theme.palette.primary.main, LEVEL_ALPHA[Math.min(level, 4)])

              const cell = (
                <Box
                  sx={{
                    width: cellSize,
                    height: cellSize,
                    borderRadius: '2px',
                    backgroundColor,
                  }}
                />
              )

              if (!inRange || !point) {
                return <Box key={date}>{cell}</Box>
              }

              return (
                <Tooltip
                  key={date}
                  title={t('dashboard.widgets.students_activity_heatmap.eventsOn', {
                    count: point.count,
                    date: dayjs(date).format('DD.MM.YYYY'),
                  })}
                  placement="top"
                  arrow
                >
                  {cell}
                </Tooltip>
              )
            })}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
